import React, { useRef, useState } from "react";
import Button from "../../Button";
import apiInstance from "../../apiInstance";
import { useSelector } from "react-redux";
import defaultDocImg from "../../../assets/images/doc.png";

const EditTicket = ({
    data,
    type = "add",
    updateTicket,
    addTicket,
    updateError,
    closePopup,
 }) => {

    const user = useSelector((state) => state.currentUser);
    const [ticket, setTicket] = useState({
        id: data?.ticket?._id || "",
        user: data?.ticket?.user?._id || user.id,
        subject: data?.ticket?.subject || '',
        description: data?.ticket?.description || '',
        status: data?.ticket?.status || 'open',
        category: data?.ticket?.category || 'website',
        priority: data?.ticket?.priority || 'medium',
    });
    const [existingAttachments, setExistingAttachments] = useState(
        data?.ticket?.attachments || []
    );
    const [newFiles, setNewFiles] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTicket((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        const totalCount = existingAttachments.length + newFiles.length + selected.length;
        if (totalCount > 5) {
            updateError("Maximum 5 attachments allowed.");
            return;
        }
        setNewFiles((prev) => [...prev, ...selected]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeNewFile = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (index) => {
        setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {

        setLoading(true);
        updateError("");
        setFieldErrors({});

        const formData = new FormData();
        formData.append("user", ticket.user);
        formData.append("subject", ticket.subject);
        formData.append("description", ticket.description);
        formData.append("status", ticket.status);
        formData.append("category", ticket.category);
        formData.append("priority", ticket.priority);
        formData.append("existingAttachments", JSON.stringify(existingAttachments));

        newFiles.forEach((file) => {
            formData.append("attachments", file);
        });

        const apiCall =
            type === "update"
                ? apiInstance.put(`/dashboard/support-ticket/${ticket.id}`, formData)
                : apiInstance.post(`/dashboard/support-ticket`, formData);

        apiCall
            .then((response) => {
                const returnedTicket = response.data.ticket;
                if (type === "update") {
                    updateTicket(returnedTicket);
                } else {
                    addTicket(returnedTicket);
                }
                closePopup();
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.error("Unexpected error:", error);
                if (error.response) {
                    if (
                        error.response.status === 400 &&
                        error.response.data.fieldErrors
                    ) {
                        setFieldErrors(error.response.data.fieldErrors);
                    } else {
                        updateError(error.response.data?.message || 'Something went wrong, please try again later');
                    }
                }
            })
    };

    return (
        <div className="mjs-pp">
            <h2>{type === "add" ? "ADD TICKET" : "EDIT TICKET"}</h2>
            <form>

                <div className="mjs-input-field">
                    <label htmlFor="subject">Subject</label>
                    <input
                        type="text"
                        placeholder="Subject"
                        name="subject"
                        value={ticket.subject}
                        onChange={handleInputChange}
                    />
                    {fieldErrors.subject && (
                        <span className="mjs-error">
                            {fieldErrors.subject}
                        </span>
                    )}
                </div>

                <div className="mjs-input-field">
                    <label htmlFor="category">Category</label>
                    <select
                        name="category"
                        value={ticket.category}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Category</option>
                        <option value="billing">Billing</option>
                        <option value="technical">Technical</option>
                        <option value="account">Account</option>
                        <option value="website">Website</option>
                        <option value="feature_request">Feature Reuqest</option>
                        <option value="other">Other</option>
                    </select>
                    {fieldErrors.category && (
                        <span className="mjsdev-error">
                            {fieldErrors.category}
                        </span>
                    )}
                </div>

                <div className="mjs-input-field">
                    <label htmlFor="description">Description</label>
                    <textarea
                        name="description"
                        value={ticket.description}
                        onChange={handleInputChange}
                    >
                        {ticket.description}
                    </textarea>
                    {fieldErrors.description && (
                        <span className="mjs-error">
                            {fieldErrors.description}
                        </span>
                    )}
                </div>

                <div className="mjs-input-field">
                    <label>Attachments</label>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx"
                        onChange={handleFileChange}
                    />
                    <small>Allowed: Images (jpg, png, gif, webp) & Documents (pdf, doc, docx). Max 10MB each, up to 5 files.</small>

                    {existingAttachments.length > 0 && (
                        <div className="mjs-attachment-list">
                            {existingAttachments.map((att, i) => (
                                <div key={att.filename} className="mjs-attachment-item">
                                    {att.mimetype?.startsWith("image/") ? (
                                        <img
                                            src={`${process.env.REACT_APP_SERVER_URL}/uploads/support-tickets/${att.filename}`}
                                            alt={att.originalName}
                                            className="mjs-attachment-thumb"
                                        />
                                    ) : (
                                        <img
                                            src={defaultDocImg}
                                            alt={att.originalName}
                                            className="mjs-attachment-thumb"
                                        />
                                    )}
                                    <span>{att.originalName}</span>
                                    <button type="button" onClick={() => removeExistingAttachment(i)}>&times;</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {newFiles.length > 0 && (
                        <div className="mjs-attachment-list">
                            {newFiles.map((file, i) => (
                                <div key={i} className="mjs-attachment-item">
                                    {file.type?.startsWith("image/") ? (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            className="mjs-attachment-thumb"
                                        />
                                    ) : (
                                        <img
                                            src={defaultDocImg}
                                            alt={file.originalName}
                                            className="mjs-attachment-thumb"
                                        />
                                    )}
                                    <span>{file.name}</span>
                                    <button type="button" onClick={() => removeNewFile(i)}>&times;</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </form>

            <div className="mjs-form-actions">
                {loading && (
                    <i
                        className="fa-solid fa-spinner fa-spin"
                    ></i>
                )}
                <Button
                    name={type === "add" ? "Create" : "Update"}
                    eventHandler={handleSubmit}
                />
                <Button name="Cancel" eventHandler={closePopup} />
            </div>
        </div>
    );
};

export default EditTicket;
